/**
 * This context should include all relevant information about the connected client.
 * It stands as a single entry point and single source of truth for all connected client information.
 */
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import deepmerge from 'deepmerge';
import jwtDecode from 'jwt-decode';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'src/lib/routing';
import { useMutation, useQuery } from 'urql';

import { IdentifyUser } from 'src/IdentifyUser';
import { useIsKinde } from 'containers/Auth/AuthWrapper';
import { OnboardingStatus } from 'src/constants/graphql-enums';
import { analytics } from 'lib/analytics';
import { localStorage } from 'lib/storage';
import { userUtils } from 'lib/user';
import { clearImpersonation, getImpersonationToken } from 'utils/impersonation';

import { AddAcquisitionSource } from './data/AddAcquisitionSource.mutation';
import { Viewer } from './data/Viewer.query';

import type { ResultOf } from 'src/graphql';
import type { UserMetaSocialType, UserResearcher, UserRoles } from 'containers/Settings/types';
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';

type ViewerResult = ResultOf<typeof Viewer>;

type AuthState = 'unknown' | 'authenticated' | 'unauthenticated';

export type ClientDetails = ({
  id?: string | null;
  email?: string | null;
  researcher?: Omit<UserResearcher, 'certification'>;
  intercomJWT?: string | null;
  name?: {
    firstname?: string | null;
    lastname?: string | null;
  } | null;
  team?: {
    id?: string | null;
    name?: string | null;
    kinde_org_id?: string | null;
  } | null;
  social?: UserMetaSocialType | null;
  phone?: string | null;
  roles?: (UserRoles | null)[] | null;
  teamRole?: number | null;
  type: {
    researcher?: boolean | null;
    client?: boolean | null;
  } | null;
  Teams?: NonNullable<ViewerResult['viewer']>['Teams'];
  isAskableInternalStaff?: boolean;
} | null) &
  ViewerResult['viewer'];

export type ConnectedClientContext = {
  authState: AuthState;
  isLoading: boolean;
  actions: {
    setAccessToken: Dispatch<SetStateAction<string | null>>;
    clear: () => void;

    updateClientDetails: (details: Partial<ClientDetails>) => void;
    refetch: () => void;
    maybeNavigateToCompleteProfile: (viewer: ViewerResult['viewer'], redirectPath: string) => boolean;
  };
  details: ClientDetails;
  isAdminDelegate: boolean;
};

const ConnectedClientDetailsContext = createContext<ConnectedClientContext | undefined>(undefined);

export function useConnectedClient() {
  const ctx = useContext(ConnectedClientDetailsContext);

  if (ctx === undefined) {
    throw new Error('useClient cannot be used outside of ClientProvider');
  }

  return ctx;
}

type Props = {
  children: ((a: { isReady: boolean; details: ClientDetails | null }) => ReactNode) | ReactNode;
  defaultClientDetails?: ClientDetails;
};

export const ConnectedClientProvider: FC<Props> = ({ children, defaultClientDetails }) => {
  // Save in state for now. So in the future when we do changes to the client details we can update here optimistically.
  const [clientDetails, setClientDetails] = useState<ClientDetails | null>(defaultClientDetails ?? null);
  const [accessToken, setAccessToken] = useState<string | null>(getImpersonationToken());
  const [needsParticipantRedirect, setNeedsParticipantRedirect] = useState(false);
  const [, addAcquisitionSource] = useMutation(AddAcquisitionSource);
  const { isAuthenticated } = useKindeAuth();
  const isKinde = useIsKinde();
  const ready = isKinde ? isAuthenticated : accessToken !== null;
  const navigate = useNavigate();
  const location = useLocation();

  const updateClientDetails = (newDetails: Partial<ClientDetails>) => {
    setClientDetails(state => deepmerge(state!, newDetails, { arrayMerge: (a, b) => b }));
  };
  const maybeNavigateToCompleteProfile = (viewer: ViewerResult['viewer'], redirectPath: string) => {
    if (
      !isKinde &&
      (!viewer?.meta?.identity?.firstname || !viewer?.meta?.identity?.lastname) &&
      location.pathname !== '/complete-profile'
    ) {
      navigate(`/complete-profile?redirect_path=${redirectPath}`);
      return true;
    }
    return false;
  };

  const isAdminDelegate = useMemo(() => {
    if (!accessToken) {
      return false;
    }
    const decoded = jwtDecode(accessToken);
    return !!(decoded && typeof decoded === 'object' && 'impersonated_by' in decoded && decoded.impersonated_by);
  }, [accessToken]);

  // Teams are manually onboarded by an Askable employee now, so anything gated to staff
  // (e.g. self-serve team creation) should key off this rather than duplicating the domain check.
  const detailsWithDerived = useMemo(() => {
    if (!clientDetails) {
      return clientDetails;
    }
    return { ...clientDetails, isAskableInternalStaff: !!clientDetails.email?.endsWith('@askable.com') };
  }, [clientDetails]);

  useEffect(() => {
    if (needsParticipantRedirect) {
      window.location.href = '/participant-redirect';
    }
  }, [needsParticipantRedirect]);

  const [{ data: viewerData, fetching: viewerLoading, error: viewerError }, reexecuteViewer] = useQuery({
    query: Viewer,
    pause: !ready,
  });

  // Handle auth errors
  useEffect(() => {
    if (viewerError?.message === 'jwt expired') {
      navigate('/logout');
    }
  }, [viewerError, navigate]);

  // Process viewer data.
  useEffect(() => {
    const viewer = viewerData?.viewer;
    if (!viewer || viewerLoading) return;

    if (viewer?.type?.participant && !window.location.pathname.startsWith('/participant-redirect')) {
      setNeedsParticipantRedirect(true);
      return;
    }

    maybeNavigateToCompleteProfile(viewer, '/');

    const utmSource = sessionStorage.getItem('utm_source');
    const utmCampaign = sessionStorage.getItem('utm_campaign');
    if (utmSource || utmCampaign) {
      void addAcquisitionSource({
        user: {
          meta: {
            acquisition: {
              ...(utmSource && { source: utmSource }),
              ...(utmCampaign && { campaign: utmCampaign }),
            },
          },
        },
      }).then(() => {
        sessionStorage.removeItem('utm_source');
        sessionStorage.removeItem('utm_campaign');
      });
    }

    const team = viewer?.ConnectedTeam;

    const teamUser = viewer?.ConnectedTeam?.users?.find(a => a?._id === viewer._id);
    const teamRole = teamUser?.role;

    updateClientDetails({
      id: viewer?._id,
      ...viewer,
      Teams: (viewer?.Teams ? [...viewer.Teams] : []).sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')),
      teamRole,
      intercomJWT: viewer?.intercomJWT,
      name: {
        firstname: viewer?.meta?.identity?.firstname,
        lastname: viewer?.meta?.identity?.lastname,
      },
      email: viewer?.email,
      team: {
        id: team?._id,
        name: team?.name,
      },
      type: viewer?.type,
      social: {
        linkedin: {
          profile_url: viewer?.meta?.social?.linkedin?.profile_url ?? '',
        },
      },
      researcher: {
        ux_length: viewer?.researcher?.ux_length,
        certification: {
          status: viewer?.researcher?.certification?.status!,
        },
        onboarding: {
          linkedIn: viewer?.researcher?.onboarding?.linkedIn ?? OnboardingStatus.Incomplete,
          ux_length: viewer?.researcher?.onboarding?.ux_length ?? OnboardingStatus.Incomplete,
          methods: viewer?.researcher?.onboarding?.methods ?? OnboardingStatus.Incomplete,
        },
      },
    } as Partial<ClientDetails>);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Callback refs are stable in behavior; only data-driven deps included
  }, [viewerData, viewerLoading]);

  const authState = useMemo<AuthState>(() => {
    if (clientDetails?.id && clientDetails?.team?.id) {
      return 'authenticated';
    }

    if (clientDetails?.id || userUtils.isDemoClient(clientDetails?.roles)) {
      return 'authenticated';
    }

    return 'unauthenticated';
  }, [clientDetails]);

  const clear = () => {
    setClientDetails(null);
    clearImpersonation();
    localStorage.reset();
    void analytics.reset();
  };

  return (
    // A user is either authenticated or unauthenticated, but we do not want to act on unauthenticated until we have completed
    // the auth resolution process (a user is unauthenticated until this is completed).
    // Auth resolution has three stages, and isLoading must remain true through all of them:
    //  1. !ready                                         — waiting for prerequisites (token)
    //  2. viewerLoading                                  — viewer query in flight
    //  3. !viewerData && !viewerError                    — query initiated but hasn't returned yet
    //  4. !!viewerData?.viewer && !clientDetails?.id      — viewer returned but useEffect hasn't processed it into clientDetails
    //
    // Stage 3 covers a gap where the query engine may not set fetching=true synchronously when pause transitions.
    // Stage 4 exists because viewer data processing was moved to a useEffect,
    // which creates a render between the query completing and clientDetails being set.
    // Without these, RequireAuth briefly sees isLoading=false + authState='unauthenticated' and redirects to /login.
    <ConnectedClientDetailsContext.Provider
      value={{
        authState,
        isLoading:
          !ready || viewerLoading || (!viewerData && !viewerError) || (!!viewerData?.viewer && !clientDetails?.id),
        details: detailsWithDerived!,
        isAdminDelegate,
        actions: {
          updateClientDetails,
          clear,
          setAccessToken,
          refetch: () => reexecuteViewer({ requestPolicy: 'network-only' }),
          maybeNavigateToCompleteProfile,
        },
      }}
    >
      <>
        {typeof children === 'function'
          ? children({
              isReady: !viewerLoading || import.meta.env.MODE === 'test',
              details: clientDetails,
            })
          : children}
        <IdentifyUser />
      </>
    </ConnectedClientDetailsContext.Provider>
  );
};
